import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import type { ComponentPropsWithRef, ElementType } from "react";

import { cn } from "helpers/cn";

const flexVariants = cva("flex", {
  variants: {
    direction: {
      row: "flex-row",
      col: "flex-col",
      "col-reverse": "flex-col-reverse"
    },
    align: {
      start: "items-start",
      center: "items-center",
      end: "items-end",
      baseline: "items-baseline",
      stretch: "items-stretch"
    },
    justify: {
      start: "justify-start",
      center: "justify-center",
      end: "justify-end",
      between: "justify-between",
      around: "justify-around",
      evenly: "justify-evenly"
    },
    gap: {
      0: "gap-0",
      0.5: "gap-0.5",
      1: "gap-1",
      1.5: "gap-1.5",
      2: "gap-2",
      2.5: "gap-2.5",
      3: "gap-3",
      4: "gap-4",
      5: "gap-5",
      6: "gap-6",
      8: "gap-8",
      12: "gap-12"
    },
    wrap: {
      wrap: "flex-wrap",
      nowrap: "flex-nowrap",
      reverse: "flex-wrap-reverse"
    },
    flex: {
      1: "flex-1",
      auto: "flex-auto",
      initial: "flex-initial",
      none: "flex-none"
    },
    divider: { top: "border-t border-border", bottom: "border-b border-border" },
    fullWidth: { true: "w-full" },
    /** A flex child's `min-width` is `auto`, so it refuses to shrink below its content and any truncation inside it silently stops working. */
    shrinkable: { true: "min-w-0" }
  },
  defaultVariants: { direction: "row" }
});

type FlexVariantProps = VariantProps<typeof flexVariants>;

type FlexProps<T extends ElementType = "div"> = {
  as?: T;
} & FlexVariantProps &
  Omit<ComponentPropsWithRef<T>, "as" | keyof FlexVariantProps>;

const Flex = <T extends ElementType = "div">({
  className,
  as,
  direction,
  align,
  justify,
  gap,
  wrap,
  flex,
  divider,
  fullWidth,
  shrinkable,
  ...props
}: FlexProps<T>) => {
  const Comp = (as ?? "div") as ElementType;

  return (
    <Comp
      className={cn(
        flexVariants({
          direction,
          align,
          justify,
          gap,
          wrap,
          flex,
          divider,
          fullWidth,
          shrinkable
        }),
        className
      )}
      data-slot="flex"
      {...props}
    />
  );
};

export { Flex, flexVariants };
export type { FlexProps };
